import { useCallback, useEffect, useRef, useState } from "react";

import { useParams } from "react-router-dom";

import { BillingDetails } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_pb";

import { getBillingDetails as getBillingDetailsApi } from "api/endpoints/subscription";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import { PillStatusMode } from "constants/index";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

export interface UseBillingDetailsResult {
  billingDetails?: BillingDetails;
  /** Which affordance BillingDetailsForm should offer: create, edit or check. */
  statusMode: PillStatusMode;
  refresh: () => void;
}

/**
 * The org's billing details, and what the form should offer to do with them.
 *
 * Both the subscription detail pane and the dashboard's Billing card show this
 * field, and the create-versus-edit decision is not obvious - a NOT_FOUND means
 * "create", any other failure means "check" - so it lives in one place.
 */
export const useBillingDetails = (
  onError?: () => void,
): UseBillingDetailsResult => {
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const [billingDetails, setBillingDetails] = useState<BillingDetails>();
  const [statusMode, setStatusMode] = useState<PillStatusMode>(
    PillStatusMode.EDIT,
  );

  // Held in a ref so a caller's inline callback does not re-run the request.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const refresh = useCallback(() => {
    getBillingDetailsApi(
      { orgId: toUUID(roleId) },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((response) => {
        setBillingDetails(response?.billingDetails);
        setStatusMode(
          response?.billingDetails
            ? PillStatusMode.EDIT
            : PillStatusMode.CREATE,
        );
      })
      .catch((err) => {
        setStatusMode(
          err.code === gRPCStatus.NOT_FOUND
            ? PillStatusMode.CREATE
            : PillStatusMode.CHECK,
        );
        onErrorRef.current?.();
      });
  }, [regionCode, roleId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { billingDetails, statusMode, refresh };
};

export default useBillingDetails;
